import { useEffect, useState } from "react";
import { toast } from "sonner";
import api from "@/api";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";
import { Users, Copy, Share2 } from "lucide-react";

const fmt = (n) => Number(n || 0).toLocaleString(undefined, { maximumFractionDigits: 2 });

export default function Team() {
  const { user } = useAuth();
  const [levels, setLevels] = useState([]);

  useEffect(() => { api.get("/team").then(({ data }) => setLevels(data.levels)); }, []);

  const refLink = `${window.location.origin}/register?ref=${user?.referral_code}`;
  const copy = () => { navigator.clipboard.writeText(refLink); toast.success("Referral link copied!"); };

  const totalEarned = levels.reduce((s, l) => s + (l.earned || 0), 0);
  const totalMembers = levels.reduce((s, l) => s + (l.count || 0), 0);

  return (
    <div>
      <h1 className="text-2xl sm:text-3xl font-bold mb-1">Team & Referral</h1>
      <p className="text-sm text-slate-400 mb-6">Multi-level referral income — automatic commission on every deposit.</p>

      <div className="card-surface rounded-2xl p-6 mb-6">
        <div className="flex items-center gap-2 mb-3"><Share2 className="h-5 w-5 text-emerald-400" /><h3 className="font-semibold">Your Invite Link</h3></div>
        <div className="flex gap-2">
          <div className="flex-1 truncate text-sm bg-white/5 rounded-lg px-3 py-2.5 font-mono">{refLink}</div>
          <Button data-testid="team-copy-referral-btn" onClick={copy} className="bg-emerald-500 hover:bg-emerald-600 shrink-0">
            <Copy className="h-4 w-4" />
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4 mb-6">
        <div className="card-surface rounded-2xl p-5">
          <div className="text-xs text-slate-400 uppercase">Total Team</div>
          <div className="text-3xl font-extrabold font-mono mt-1" data-testid="team-total-members">{totalMembers}</div>
        </div>
        <div className="card-surface rounded-2xl p-5">
          <div className="text-xs text-slate-400 uppercase">Referral Earned</div>
          <div className="text-3xl font-extrabold font-mono text-emerald-400 mt-1" data-testid="team-total-earned">${fmt(totalEarned)}</div>
        </div>
      </div>

      <div className="space-y-6">
        {levels.map((l) => (
          <div key={l.level} className="card-surface rounded-2xl p-6" data-testid={`team-level-${l.level}`}>
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <Users className="h-5 w-5 text-emerald-400" />
                <span className="font-semibold">Level {l.level}</span>
                <span className="text-xs bg-emerald-500/10 text-emerald-400 px-2 py-0.5 rounded-full">{l.pct}% commission</span>
              </div>
              <div className="text-sm text-slate-400">{l.count} members · <span className="text-emerald-400 font-mono">${fmt(l.earned)}</span></div>
            </div>
            {l.members.length === 0 ? (
              <div className="text-sm text-slate-500">No members at this level yet.</div>
            ) : (
              <div className="space-y-2">
                {l.members.map((m, i) => (
                  <div key={i} className="flex items-center justify-between text-sm bg-white/5 rounded-lg px-3 py-2">
                    <span>{m.name}</span>
                    <span className="text-slate-400 text-xs">{m.email}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
